import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Page,
  useFlags,
  useSnackbarContext,
} from "@inductiveautomation/ws-components";

import AccountAppBar from "../../../components/AccountAppBar/AccountAppBar";
import EmailPreferencesForm from "../../../components/EmailPreferencesForm/EmailPreferencesForm";
import HubspotEmailPreferences from "./HubspotEmailPreferences";
import { LastRequestId } from "./EmailPreferencesAuth.types";
import { useEmailQueries } from "./useEmailQueries";
import { idForKey, mapResponseToIds } from "./utilities";
import { useCurrentUser } from "../../../hooks/currentUser";
import { emptyState } from "./EmailPreferencesAuth.data";
import { AccountFlags } from "../../../featureFlags";
import {
  useGetCommunicationQuery,
  EmailPreferencesResponse,
} from "../api/emailPreferences.api";

const determineIdToDisable = (
  isLoading: boolean,
  lastRequest: LastRequestId | keyof EmailPreferencesResponse | undefined
) => {
  if (!isLoading || !lastRequest) return undefined;

  return lastRequest === "unsubscribe_all"
    ? "unsubscribe_all"
    : idForKey[lastRequest as keyof EmailPreferencesResponse];
};

const EmailPreferencesAuth = () => {
  const { t } = useTranslation();
  const { addSnackbar } = useSnackbarContext();
  const { tsAccountHubspotEmailPrefsFe, tsAccountWorkspaceVisibilityFe } =
    useFlags<AccountFlags>();
  const [displayedSuccess, setDisplayedSuccess] = useState(false);
  const [displayedError, setDisplayedError] = useState(false);

  const {
    updateSubscription,
    unsubscribeAll,
    isLoading,
    isError,
    isSuccess,
    errors,
    lastRequest,
  } = useEmailQueries();

  const { data: communicationData, error: queryError } =
    useGetCommunicationQuery();
  const data = communicationData ?? emptyState;
  const userInfo = useCurrentUser();
  const primaryEmailAddress =
    !userInfo.isLoading && userInfo.user?.primaryEmailAddress
      ? userInfo.user.primaryEmailAddress
      : "";
  const selectedIds = mapResponseToIds(data);

  useEffect(() => {
    if (isSuccess && !displayedSuccess) {
      addSnackbar(t("email_preferences.success_message"), {
        id: "email-preferences-success",
      });
      setDisplayedSuccess(true);
    }

    if (!isSuccess) {
      setDisplayedSuccess(false);
    }
  }, [isSuccess, lastRequest, displayedSuccess, addSnackbar, t]);

  useEffect(() => {
    if (isError && !displayedError) {
      console.error(errors);
      addSnackbar(t("email_preferences.error_message"), {
        type: "error",
        id: "email-preferences-error",
      });
      setDisplayedError(true);
    } else if (!isError) {
      setDisplayedError(false);
    }
  }, [isError, errors, displayedError, addSnackbar, t]);

  const idToDisable =
    communicationData === undefined || !!queryError
      ? "initial_load"
      : determineIdToDisable(isLoading, lastRequest);

  return (
    <>
      <AccountAppBar
        title={[
          {
            label: "Email Preferences",
            id: "account-app-bar-email-preferences",
            onClick: () => undefined,
          },
        ]}
      />
      {tsAccountHubspotEmailPrefsFe ? (
        <HubspotEmailPreferences
          primaryEmailAddress={primaryEmailAddress}
          vendorEmailPreferencesManagementUrl={
            communicationData?.vendorEmailPreferencesManagementUrl
          }
          showWorkspaceActivity={tsAccountWorkspaceVisibilityFe}
          selectedIds={selectedIds}
          idToDisable={idToDisable}
          isLoading={idToDisable === "initial_load"}
          onChange={({ id, value }) => {
            setDisplayedSuccess(false);
            updateSubscription(id, value);
          }}
        />
      ) : (
        <Page
          sections={[
            {
              heading: t("email_preferences.title"),
              headingLevel: "h1",
              description: (
                <Trans i18nKey="email_preferences.subtitle">
                  {{ email: primaryEmailAddress }}
                </Trans>
              ),
              children: (
                <>
                  <EmailPreferencesForm
                    inputType="switch"
                    idToDisable={idToDisable}
                    selectedIds={selectedIds}
                    onChange={({ id, value }) => {
                      setDisplayedSuccess(false);
                      updateSubscription(id, value);
                    }}
                    onUnsubscribeAll={() => {
                      setDisplayedSuccess(false);
                      unsubscribeAll();
                    }}
                  />
                </>
              ),
            },
          ]}
        />
      )}
    </>
  );
};

export default EmailPreferencesAuth;
